import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  updateInviteCodeExpiresAt,
} from "@/features/billing/api/billing";
import { ApiError } from "@/api/fetch";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const toDatetimeLocalValue = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const errorMessage = (err: unknown) =>
  err instanceof ApiError ? err.message : "Failed to update expiration";

type EditExpiryDialogProps = {
  code: string;
  expiresAt: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
};

export const EditExpiryDialog = ({
  code,
  expiresAt,
  open,
  onOpenChange,
  onUpdated,
}: EditExpiryDialogProps) => {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setValue(toDatetimeLocalValue(expiresAt));
  }, [open, expiresAt]);

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!value.trim() || submitting) return;
      setSubmitting(true);
      try {
        await updateInviteCodeExpiresAt(code, {
          expires_at: new Date(value).toISOString(),
        });
        toast.success("Expiration updated");
        onOpenChange(false);
        onUpdated();
      } catch (err) {
        toast.error(errorMessage(err));
      } finally {
        setSubmitting(false);
      }
    },
    [value, submitting, code, onOpenChange, onUpdated],
  );

  const handleRemove = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await updateInviteCodeExpiresAt(code, { expires_at: null });
      toast.success("Expiration removed");
      onOpenChange(false);
      onUpdated();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [submitting, code, onOpenChange, onUpdated]);

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit expiration</DialogTitle>
          <DialogDescription>
            Set when the code <code>{code}</code> stops working — a past date
            expires it immediately. Remove the expiration to make it valid
            indefinitely.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <label className="block space-y-1" htmlFor="edit-expiry-date">
            <span className="text-sm font-medium text-zinc-500">
              Expires at
            </span>
            <Input
              id="edit-expiry-date"
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            {expiresAt != null && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleRemove}
                disabled={submitting}
              >
                Remove expiration
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={submitting || !value.trim()}
            >
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};